import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'

/**
 * 一覧のステータス絞り込みの選択肢 (plan-common.md §1「ステータス絞り込みの振り分け」)。
 * 提出管理画面の絞込 (`SubmissionManagement/models/submissionStatusFilter.ts`) とは
 * 提出済 / 受領済を分ける点と、承認待ちを利用者で分けない点が違う。
 */
export type PlanListStatusFilter =
  | 'all'
  | 'actionRequired'
  | 'unsubmitted'
  | 'waiting'
  | 'submitted'
  | 'received'

/** 表示順 (spec: すべて / 要対応 / 未提出 / 待ち / 提出済 / 受領済)。 */
export const PLAN_LIST_STATUS_FILTER_OPTIONS: readonly PlanListStatusFilter[] =
  ['all', 'actionRequired', 'unsubmitted', 'waiting', 'submitted', 'received']

type PlanListStatusCategory = Exclude<PlanListStatusFilter, 'all'>

/**
 * 不整合以外の提出ステータスの振り分け。不整合は計画単位の事実で分けるので
 * classifyPlanListStatus が持つ。UNSPECIFIED (未知値の BFF fallback) と編集中は、
 * バッジ (`deriveStatusBadge`) と同じく未提出に入れる。
 */
const CATEGORY_OF_STATUS: Record<
  Exclude<PlanSubmissionStatus, PlanSubmissionStatus.INCONSISTENT>,
  PlanListStatusCategory
> = {
  [PlanSubmissionStatus.UNSPECIFIED]: 'unsubmitted',
  [PlanSubmissionStatus.GENERATED]: 'unsubmitted',
  [PlanSubmissionStatus.EDITING]: 'unsubmitted',
  [PlanSubmissionStatus.REJECTED]: 'actionRequired',
  [PlanSubmissionStatus.GENERATION_FAILED]: 'actionRequired',
  [PlanSubmissionStatus.SUBMIT_FAILED]: 'actionRequired',
  [PlanSubmissionStatus.SEND_FAILED]: 'actionRequired',
  [PlanSubmissionStatus.GENERATING]: 'waiting',
  [PlanSubmissionStatus.APPROVAL_PENDING]: 'waiting',
  [PlanSubmissionStatus.SUBMITTING]: 'waiting',
  [PlanSubmissionStatus.SUBMITTED]: 'submitted',
  [PlanSubmissionStatus.ACCEPTED]: 'received',
}

type ClassifiableRow = {
  status: PlanSubmissionStatus
  counterpartyMismatchesAllResolved: boolean
}

/**
 * 行が入る選択肢を返す。不整合は、取引相手間ミスマッチを全て解消済みにした計画なら
 * 計画内不整合が残っていても受領済 (plan-common.md §1)。
 */
export function classifyPlanListStatus(
  row: ClassifiableRow,
): PlanListStatusCategory {
  if (row.status === PlanSubmissionStatus.INCONSISTENT) {
    return row.counterpartyMismatchesAllResolved ? 'received' : 'actionRequired'
  }
  // 型に無い wire 値 (deploy skew) はバッジと同じく未提出に倒す。
  return CATEGORY_OF_STATUS[row.status] ?? 'unsubmitted'
}

/** 選択肢に入る行だけを返す (順序は保つ)。'all' は全行。 */
export function filterByPlanListStatus<T extends { row: ClassifiableRow }>(
  items: T[],
  option: PlanListStatusFilter,
): T[] {
  if (option === 'all') {
    return items
  }
  return items.filter((item) => classifyPlanListStatus(item.row) === option)
}

/** 選択肢ごとの件数。「すべて」は全行数。 */
export function countPlanListStatusFilters(
  items: ReadonlyArray<{ row: ClassifiableRow }>,
): Record<PlanListStatusFilter, number> {
  const counts: Record<PlanListStatusFilter, number> = {
    all: items.length,
    actionRequired: 0,
    unsubmitted: 0,
    waiting: 0,
    submitted: 0,
    received: 0,
  }
  for (const item of items) {
    counts[classifyPlanListStatus(item.row)] += 1
  }
  return counts
}
